import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ICollegeListUploadPayload } from 'Src/ng2/school/data-uploads/college-list/college-list-upload.component';
import { IFlag } from '../typings/interfaces/flags.interface';
import { ActivitiesModalComponent, IActivitiesModalComponentData } from './activities/activities-modal.component';
import {
  ActivityStatusModalComponent,
  IActivityStatusModalComponentData,
} from './activities/activity-status-modal/activity-status-modal.component';
import { AddCareerModalComponent } from './add-career/add-career.component';
import { AlertModalcomponent } from './alert/alert-modal.component';
import {
  AssignSupportModalComponent,
  IAssignSupportModalComponentData,
} from './assign-support/assign-support-modal.component';
import { BatchEditModalComponent } from './batch-edit/batch-edit-modal.component';
import { ConfirmModalComponent, IConfirmModalComponentData } from './confirm/confirm-modal.component';
import {
  CsvUploadConfirmModalComponent,
  IConfirmUploadModalComponentData,
} from './csv-upload/confirm-modal/csv-upload-confirm-modal.component';
import { CsvUploadErrorModalComponent } from './csv-upload/error-modal/csv-upload-error-modal.component';
import { CsvUploadFixErrorsModalComponent } from './csv-upload/fix-errors-modal/csv-upload-fix-errors-modal.component';
import { ErrorModalComponent } from './error/error-modal.component';
import { FlagIgnoreModalComponent } from './flag-ignore/flag-ignore-modal.component';
import { FlagLogActionModalComponent } from './flag-log-action/flag-log-action-modal.component';
import { HistoryAllModalComponent, IHistoryModalData } from './history-all/history-all-modal.component';
import { IListSummaryData, ListSummaryModalComponent } from './list-summary/list-summary-modal.component';
import { EOPMilestoneModalComponent } from './milestone/eop-milestone-modal/eop-milestone-modal.component';
import { FinalDecisionMilestoneModalComponent } from './milestone/final-decision-milestone-modal/final-decision-milestone-modal.component';
import {
  MODALS_CONFIG,
  MODALS_CONFIG_COMMON_MARKUP,
  MODALS_CONFIG_MED,
  MODALS_CONFIG_WIDE,
  MODALS_CONFIG_WIDE_NO_PADDING,
} from './modals.config';
import { SyncedSheetColumnsModalComponent } from './other-tools/synced-sheet-columns/synced-sheet-columns-modal.component';
import { SyncedSheetModalComponent } from './other-tools/synced-sheet/synced-sheet-modal.component';
import { ShelterClusterUserModalShellComponent } from './shelter-user-management/shelter-cluster-user/shelter-cluster-user-modal-shell/shelter-cluster-user-modal-shell.component';
import {
  IShelterClusterUserModalShellData,
  TShelterClusterUserModalViewMode,
} from './shelter-user-management/shelter-cluster-user/shelter-cluster-user-modals.config';
import {
  IRemoveStudentActivitiesModalComponentData,
  RemoveStudentActivityModalComponent,
} from './student-activities/remove-student-activity/remove-student-activity-modal.component';
import {
  IStudentActivitiesModalComponentData,
  StudentActivitiesModalComponent,
} from './student-activities/student-activities-modal.component';
import {
  IStudentActivityStatusModalComponentData,
  StudentActivityStatusModalComponent,
} from './student-activities/student-activity-status/student-activity-status-modal.component';
import {
  IStudentPathsModalComponentData,
  StudentPathsModalComponent,
} from './student-paths/student-paths-modal.component';
import { StudentReportModalComponent } from './student-report/student-report.component';
import { ClusterUserModalShellComponent } from './user-management/cluster-user/cluster-user-modal-shell/cluster-user-modal-shell.component';
import {
  IClusterUserModalShellData,
  TClusterUserModalViewMode,
} from './user-management/cluster-user/cluster-user-modals.config';

export interface IBaseModalData {
  studentIds?: string[];
  schoolId?: string;
  isProfileMode?: boolean;
}

@Injectable()
export class ModalsService {
  constructor(public dialog: MatDialog) {}

  // Mat Dialog Configurations
  readonly MODALS_CONFIG = MODALS_CONFIG;
  readonly MODALS_CONFIG_COMMON_MARKUP = MODALS_CONFIG_COMMON_MARKUP;

  private getModalConfig(data?): MatDialogConfig {
    const config = data ? { ...this.MODALS_CONFIG, ...{ data } } : this.MODALS_CONFIG;
    return config;
  }

  private getCommonMarkupModalConfig(data?): MatDialogConfig {
    const config = data ? { ...this.MODALS_CONFIG_COMMON_MARKUP, ...{ data } } : this.MODALS_CONFIG_COMMON_MARKUP;
    return config;
  }

  readonly panelClass = MODALS_CONFIG.panelClass;
  readonly autoFocus = MODALS_CONFIG.autoFocus;
  readonly wideClass = MODALS_CONFIG_WIDE.panelClass;
  readonly medClass = MODALS_CONFIG_MED.panelClass;
  readonly noPaddingWideClass = MODALS_CONFIG_WIDE_NO_PADDING.panelClass;

  public openFlagsLogActionModal(flag?: IFlag): MatDialogRef<FlagLogActionModalComponent> {
    const modalConfig = this.getModalConfig(flag);
    const modalRef = this.dialog.open(FlagLogActionModalComponent, modalConfig);

    return modalRef;
  }

  public openFlagsIgnoreModal(flag: IFlag): MatDialogRef<FlagIgnoreModalComponent> {
    const modalConfig = this.getModalConfig(flag);
    const modalRef = this.dialog.open(FlagIgnoreModalComponent, modalConfig);

    return modalRef;
  }

  public openHistoryModal(data: IHistoryModalData): MatDialogRef<HistoryAllModalComponent> {
    const modalRef = this.dialog.open(HistoryAllModalComponent, {
      panelClass: this.wideClass,
      data,
    });

    return modalRef;
  }

  public openAlertModal(data): MatDialogRef<AlertModalcomponent> {
    const modalConfig = this.getModalConfig(data);
    const modalRef = this.dialog.open(AlertModalcomponent, modalConfig);

    return modalRef;
  }

  public openAddCareerModal(data): MatDialogRef<AddCareerModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(AddCareerModalComponent, modalConfig);
    return modalRef;
  }

  public openStudentActivitiesModal(
    data: IStudentActivitiesModalComponentData,
  ): MatDialogRef<StudentActivitiesModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(StudentActivitiesModalComponent, modalConfig);
    return modalRef;
  }

  public openRemoveStudentActivitiesModal(
    data: IRemoveStudentActivitiesModalComponentData,
  ): MatDialogRef<RemoveStudentActivityModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(RemoveStudentActivityModalComponent, modalConfig);
    return modalRef;
  }

  public openActivitiesModal(data: IActivitiesModalComponentData): MatDialogRef<ActivitiesModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(ActivitiesModalComponent, modalConfig);
    return modalRef;
  }

  public openEOPMilestoneModal(data): MatDialogRef<EOPMilestoneModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(EOPMilestoneModalComponent, modalConfig);
    return modalRef;
  }

  public openFinalDecisionMilestoneModal(data): MatDialogRef<FinalDecisionMilestoneModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(FinalDecisionMilestoneModalComponent, modalConfig);
    return modalRef;
  }

  public openBatchEditModal(data?: IBaseModalData): MatDialogRef<BatchEditModalComponent> {
    const baseModalConfig = this.getCommonMarkupModalConfig(data);
    const additionalConfig = { disableClose: false };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(BatchEditModalComponent, modalConfig);
    return modalRef;
  }

  public openStudentReportModal(data: IBaseModalData): MatDialogRef<StudentReportModalComponent> {
    const baseModalConfig = this.getCommonMarkupModalConfig(data);
    const additionalConfig = { disableClose: false };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(StudentReportModalComponent, modalConfig);
    return modalRef;
  }

  // Ideally, this modal would return a StudentSupport - however, studentSupports currently live in the v2 store,
  // so the result is the selected support so that we can use `sdcActions#createStudentSupportsAsync` when
  // subscribing to the `afterClosed` event of this modal (CM).
  // redundant for now...

  // the payload interface extends IBaseModalData (directly used by some of the other modal methods in this service - JYR

  public openAssignSupportModal(data: IAssignSupportModalComponentData): MatDialogRef<AssignSupportModalComponent> {
    const baseModalConfig = this.getCommonMarkupModalConfig(data);
    const additionalConfig = { disableClose: false };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(AssignSupportModalComponent, modalConfig);
    return modalRef;
  }

  public openErrorModal(data): MatDialogRef<ErrorModalComponent> {
    const baseModalConfig = this.getCommonMarkupModalConfig(data);
    const additionalConfig = { disableClose: true };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(ErrorModalComponent, modalConfig);
    return modalRef;
  }

  public openConfirmModal(data: IConfirmModalComponentData, mode?: string): MatDialogRef<ConfirmModalComponent> {
    let baseModalConfig = this.getCommonMarkupModalConfig(data);
    if (mode === 'med') {
      baseModalConfig = {
        data,
        panelClass: this.medClass,
      };
    }
    const modalRef = this.dialog.open(ConfirmModalComponent, baseModalConfig);
    return modalRef;
  }

  public openListSummaryModal(data: IListSummaryData): MatDialogRef<ListSummaryModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(ListSummaryModalComponent, modalConfig);
    return modalRef;
  }

  public openStudentPathsModal(data: IStudentPathsModalComponentData): MatDialogRef<StudentPathsModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(StudentPathsModalComponent, modalConfig);
    return modalRef;
  }

  public openActivityStatusModal(data: IActivityStatusModalComponentData): MatDialogRef<ActivityStatusModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(ActivityStatusModalComponent, modalConfig);
    return modalRef;
  }

  public openStudentActivityStatusModal(
    data: IStudentActivityStatusModalComponentData,
  ): MatDialogRef<StudentActivityStatusModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(StudentActivityStatusModalComponent, modalConfig);
    return modalRef;
  }

  public openCsvUploadsErrorModal(data, context?: string): MatDialogRef<CsvUploadErrorModalComponent> {
    const configData = context ? { ...data, context } : data;
    const modalConfig = this.getCommonMarkupModalConfig(configData);
    const modalRef = this.dialog.open(CsvUploadErrorModalComponent, modalConfig);
    return modalRef;
  }

  public openCsvUploadsFixErrorsModal(data: ICollegeListUploadPayload): MatDialogRef<CsvUploadFixErrorsModalComponent> {
    const modalRef = this.dialog.open(CsvUploadFixErrorsModalComponent, {
      panelClass: this.wideClass,
      data,
    });
    return modalRef;
  }

  public openCsvConfirmModal(data: IConfirmUploadModalComponentData): MatDialogRef<CsvUploadConfirmModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(CsvUploadConfirmModalComponent, modalConfig);
    return modalRef;
  }

  public createSyncedSheetModal(data): MatDialogRef<SyncedSheetModalComponent> {
    const modalConfig = this.getCommonMarkupModalConfig(data);
    const modalRef = this.dialog.open(SyncedSheetModalComponent, modalConfig);
    return modalRef;
  }

  public createSyncedSheetColumnsModal(data): MatDialogRef<SyncedSheetColumnsModalComponent> {
    const modalRef = this.dialog.open(SyncedSheetColumnsModalComponent, {
      panelClass: this.wideClass,
      data,
    });
    return modalRef;
  }

  public openClusterUserShellModal(
    data: IClusterUserModalShellData,
    mode: TClusterUserModalViewMode,
  ): MatDialogRef<ClusterUserModalShellComponent> {
    let baseModalConfig = this.getCommonMarkupModalConfig(data);
    if (mode === 'PORTFOLIO') {
      baseModalConfig.panelClass = this.noPaddingWideClass;
    }
    const additionalConfig = { disableClose: true };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(ClusterUserModalShellComponent, modalConfig);
    return modalRef;
  }

  public closeExistingModals() {
    const dialogExists = this.dialog.openDialogs.length > 0;
    if (dialogExists) {
      this.dialog.closeAll();
    }
  }

  public openShelterClusterUserShellModal(
    data: IShelterClusterUserModalShellData,
    mode: TShelterClusterUserModalViewMode,
  ): MatDialogRef<ShelterClusterUserModalShellComponent> {
    let baseModalConfig = this.getCommonMarkupModalConfig(data);
    if (mode === 'PORTFOLIO') {
      baseModalConfig.panelClass = this.noPaddingWideClass;
    }
    const additionalConfig = { disableClose: true };
    const modalConfig = { ...baseModalConfig, ...additionalConfig };
    const modalRef = this.dialog.open(ShelterClusterUserModalShellComponent, modalConfig);
    return modalRef;
  }
}
